/**
 * patchOdometer — PATCH a CallLog row with corrected odometer
 * readings. Used both from the inline manual-edit form and the
 * Edit Visit page. Depends on the parent visit having synced.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, put, getAll } from '../../utils/indexedDB';
import {
  jsonHeaders,
  classifyResponse,
  networkErrorResult,
  resolveCallLogUrl,
} from '../httpHelpers';

export interface PatchOdometerInput {
  visitLocalId: string;
  driveodostart?: number;
  driveodoend?: number;
}

export interface PatchOdometerResponse {
  calllogid: number;
}

export const patchOdometerOp: WriteOp<
  PatchOdometerInput,
  PatchOdometerInput,
  PatchOdometerResponse
> = {
  kind: 'patchOdometer',

  async applyLocal(input) {
    const rows = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<Record<string, unknown>>;
    const match = rows.find((r) => r.uuid === input.visitLocalId);
    if (!match) return;
    await put(STORES.VISIT_TASK_SESSIONS, {
      ...match,
      startOdometer: input.driveodostart ?? match.startOdometer,
      endOdometer: input.driveodoend ?? match.endOdometer,
    });
  },

  serialize(input, localId) {
    return {
      action: 'patchOdometer',
      endpoint: '/api/projects/calllog/{calllogid}/',
      method: 'PATCH',
      payload: input,
      priority: 30,
      maxRetries: 10,
      dependsOn: input.visitLocalId,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as PatchOdometerInput;
    const urlOrShortCircuit = await resolveCallLogUrl(row, input.visitLocalId);
    if (typeof urlOrShortCircuit !== 'string') return urlOrShortCircuit;
    const url = urlOrShortCircuit;

    const body: Record<string, unknown> = {};
    if (input.driveodostart !== undefined) body.driveodostart = input.driveodostart;
    if (input.driveodoend !== undefined) body.driveodoend = input.driveodoend;

    let response: Response;
    try {
      response = await fetch(url, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify(body),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;
    return { status: 'success', retriable: false };
  },

  async reconcile() {},

  dependsOn(input) {
    return input.visitLocalId;
  },
};

export type PatchOdometerOp = typeof patchOdometerOp;
